import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { eur, localMoney } from "../lib/format";
import { useLocale } from "../lib/useLocale";
import { EarlyBirdBanner } from "../components/EarlyBirdCountdown";
import { WildlifeBackdrop } from "../components/WildlifeBackdrop";
import { FollowUpSection } from "../components/FollowUpSection";
import { ArrowRight, ArrowUpRight, MapPin, ChevronDown } from "lucide-react";

const round10 = (n) => Math.round(n / 10) * 10;

const POSTER = "https://images.unsplash.com/photo-1646894232861-a0ad84f1ad5d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const EXP_IMGS = [
  "https://images.unsplash.com/photo-1780283574760-e8d7fd944da5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1518708909080-704599b19972?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1543489822-c49534f3271f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1764012393975-9635be075343?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
];

export default function Landing() {
  const { t } = useTranslation();
  const { lang, path: lp } = useLocale();
  const [packages, setPackages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/areas").then((r) => setAreas(r.data)).catch(() => {});
    api.get("/experiences").then((r) => setExperiences(r.data)).catch(() => {});
  }, []);

  const expCopy = t("land.exp", { returnObjects: true });
  const pc = t("land.pricing", { returnObjects: true });

  return (
    <div data-testid="landing-page">
      <Helmet>
        <title>{t("land.metaTitle")}</title>
        <meta name="description" content={t("land.metaDesc")} />
        <meta property="og:title" content={t("land.metaTitle")} />
        <meta property="og:description" content={t("land.ogDesc")} />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="relative h-[100vh] min-h-[640px] w-full overflow-hidden -mt-[74px] grain">
        <video autoPlay muted loop playsInline poster={POSTER} className="absolute inset-0 w-full h-full object-cover">
          <source src="/media/ocean.mp4" type="video/mp4" />
          <source src="/media/ocean-sd.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-center pb-10">
          <div className="hairline mb-7 fade-up" />
          <span className="overline fade-up">{t("land.heroOverline")}</span>
          <h1 className="font-heading text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.9] tracking-tight mt-5 max-w-5xl text-rr-ivory fade-up" style={{ animationDelay: "120ms" }}>
            {t("land.heroTitleA")}<br /><span className="italic text-rr-gold">{t("land.heroQuiet")}</span> {t("land.heroTitleB")}
          </h1>
          <p className="text-rr-ivory/80 text-base md:text-lg max-w-xl mt-8 leading-relaxed fade-up" style={{ animationDelay: "240ms" }}>{t("land.heroSub")}</p>
          <div className="flex flex-wrap gap-4 mt-10 fade-up" style={{ animationDelay: "360ms" }}>
            <Link to={lp("/retreats")} data-testid="hero-explore-btn" className="btn-gold px-10 py-4 flex items-center gap-2 text-xs uppercase">
              {t("land.heroCta")} <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
          <div className="mt-9 fade-up" data-testid="hero-detours" style={{ animationDelay: "480ms" }}>
            <p className="text-[10px] uppercase tracking-[0.28em] text-rr-ivory/45 mb-3">{t("land.detoursLabel")}</p>
            <div className="flex flex-wrap gap-2.5">
              {t("land.detours", { returnObjects: true }).map((d) => (
                <span key={d} className="text-xs text-rr-ivory/80 border border-rr-ivory/25 rounded-full px-4 py-1.5 backdrop-blur-sm bg-rr-bg/20">{d}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-rr-ivory/50 scroll-cue">
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <ChevronDown className="w-4 h-4" strokeWidth={1.4} />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-8 py-28 md:py-40 text-center">
        <div className="hairline mx-auto mb-8" />
        <span className="overline">{t("land.ethosOverline")}</span>
        <p className="font-heading text-3xl md:text-5xl leading-[1.15] mt-7 text-rr-ivory">{t("land.ethos")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div className="mb-12"><span className="overline">{t("land.bmOverline")}</span><h2 className="font-heading text-5xl md:text-6xl mt-3 text-rr-ivory">{t("land.bmTitle")}</h2></div>
        <div className="grid md:grid-cols-2 gap-6">
          {expCopy.map((e, i) => (
            <div key={e.title} className={`group relative overflow-hidden rounded-xl ${i % 3 === 0 ? "md:col-span-2 h-[400px]" : "h-[360px]"}`}>
              {i === 3 ? (
                <WildlifeBackdrop />
              ) : (
                <img src={EXP_IMGS[i]} alt={e.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
              )}
              <div className="absolute inset-0 hero-overlay-soft" />
              <div className="absolute bottom-0 left-0 p-8">
                <h3 className="font-heading text-4xl text-rr-ivory">{e.title}</h3>
                <p className="text-rr-ivory/70 text-sm mt-2 max-w-md">{e.copy}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <FollowUpSection />

      {experiences.length > 0 && (() => {
        const r = experiences[0];
        const fc = t("land.flagship", { returnObjects: true });
        return (
        <section data-testid="pricing-strip" className="max-w-6xl mx-auto px-6 md:px-10 pb-28">
          <div className="mb-10">
            <span className="overline">{fc.overline}</span>
            <h2 className="font-heading text-5xl md:text-6xl mt-3 text-rr-ivory">{fc.title}</h2>
            <p className="text-rr-ivory/60 text-base mt-4 max-w-xl">{fc.sub}</p>
          </div>
          <EarlyBirdBanner className="mb-10" />
          <div className="rounded-2xl border border-rr-gold/40 bg-rr-gold/[0.04] p-8 md:p-10 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center" data-testid="flagship-card">
            <div>
              <p className="overline">{r.tier}</p>
              <h3 className="font-heading text-4xl text-rr-ivory mt-2">{r.name}</h3>
              <p className="text-rr-gold text-sm mt-2">{r.subtitle}</p>
              <div className="flex flex-wrap gap-x-10 gap-y-4 mt-7">
                <div>
                  <p className="text-xs text-rr-muted">{fc.shared}</p>
                  <p className="font-heading text-3xl text-rr-gold">{eur(r.price_from_eur)} <span className="text-sm text-rr-ivory/45">{fc.perPerson}</span></p>
                  {localMoney(r.price_from_eur, lang) && <p className="text-rr-muted text-xs">{localMoney(r.price_from_eur, lang)}</p>}
                </div>
                <div>
                  <p className="text-xs text-rr-muted">{fc.private}</p>
                  <p className="font-heading text-3xl text-rr-gold">{eur(r.price_private_eur)} <span className="text-sm text-rr-ivory/45">{fc.perPerson}</span></p>
                  {localMoney(r.price_private_eur, lang) && <p className="text-rr-muted text-xs">{localMoney(r.price_private_eur, lang)}</p>}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Link to={lp("/retreats")} data-testid="flagship-cta" className="btn-gold px-6 py-3.5 text-[11px] uppercase flex items-center justify-center gap-2">{fc.cta} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} /></Link>
              <Link to={lp("/retreats")} data-testid="bespoke-cta" className="btn-outline px-6 py-3.5 text-[11px] uppercase flex items-center justify-center gap-2">{fc.bespokeCta} <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} /></Link>
              <p className="text-[11px] text-rr-ivory/50 text-center mt-1">{fc.note}</p>
            </div>
          </div>
        </section>
        );
      })()}

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div className="flex items-end justify-between mb-12">
          <div><span className="overline">{t("land.featOverline")}</span><h2 className="font-heading text-5xl md:text-6xl mt-3 text-rr-ivory">{t("land.featTitle")}</h2></div>
          <Link to={lp("/retreats")} className="hidden md:flex items-center gap-2 text-rr-gold text-xs uppercase tracking-wide link-underline">{t("land.viewAll")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {packages.slice(0, 3).map((p) => (
            <Link key={p.slug} to={lp(`/retreats/${p.slug}`)} data-testid={`featured-${p.slug}`} className="group block card-lift">
              <div className="relative h-[440px] overflow-hidden rounded-xl">
                <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
                <div className="absolute inset-0 hero-overlay-soft" />
                <div className="absolute top-5 left-5 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-rr-gold" strokeWidth={1.4} /><span className="text-sm text-rr-ivory/90">{p.area}</span></div>
                <div className="absolute bottom-5 left-6 right-6">
                  <h3 className="font-heading text-3xl text-rr-ivory">{p.name}</h3>
                  <p className="text-rr-ivory/70 text-sm mt-1.5 line-clamp-2">{p.tagline}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-32">
        <div className="hairline mb-6" />
        <span className="overline">{t("land.areasOverline")}</span>
        <h2 className="font-heading text-5xl md:text-6xl mt-3 mb-12 text-rr-ivory">{t("land.areasTitle")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((a) => (
            <div key={a.slug} className="border-t border-rr-gold/40 pt-5">
              <p className="font-heading text-3xl text-rr-ivory">{a.name}</p>
              <p className="overline mt-1.5 mb-3">{a.region}</p>
              <p className="text-sm text-rr-muted leading-relaxed">{a.blurb}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
